import {ChangeDetectorRef, Component, Input, NgZone, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {Route} from '../../enum/route';
import {Store} from '@ngrx/store';
import {ProposalAppState} from '../../store/proposal/reducers/proposal.reducer';
import {
  selectDetailPageHasRejected,
  selectDetailPageOffers,
  selectDetailPageProposal,
  selectDetailPageProposalLoading,
  selectDetailPageProposalTile
} from '../../store/proposal/selectors/proposal.selectors';
import {ProposalDetailPageActions} from '../../store/proposal/actions/proposal.actions';
import {Permissions} from '../../enum/permissions';
import {map, Observable, pairwise, startWith, take} from 'rxjs';
import {DetailsPageTab} from '../../enum/details-page-tabs';
import {ProposalTile} from '../../model/proposal-tile';
import {Proposal} from '../../model/proposal/proposal';
import {selectUserPermissions} from '../../store/auth/selectors/auth.selectors';
import {UserAppState} from '../../store/user/reducers/user.reducer';
import {OfferStatus} from '../../enum/offer-status';
import {PermissionService} from '../../services/permission/permission.service';
import {ProposalStatus} from '../../enum/proposal-status';
import {selectUser} from '../../store/user/selectors/user.selectors';
import {User} from '../../model/user';
import {OffersListTabComponent} from '../../components/proposal-details-components/offers-list-tab/offers-list-tab.component';
import {Roles} from '../../enum/roles';
import {OfferType} from '../../enum/offer-type';


@Component({
    selector: 'app-proposal-details',
    templateUrl: './proposal-details.component.html',
    styleUrls: ['./proposal-details.component.scss', '../../../styles/form-input.scss'],
    standalone: false
})
export class ProposalDetailsComponent implements OnInit {

  allDetailsOpen: boolean = false;
  proposalTile$ = this.proposalStore.select(selectDetailPageProposalTile);
  proposal$ = this.proposalStore.select(selectDetailPageProposal);
  loading$ = this.userStore.select(selectDetailPageProposalLoading);
  activeUserId: string;

  user: User;
  user$ = this.userStore.select(selectUser);

  hasAcceptedOffers$: Observable<boolean> = this.proposalStore.select(selectDetailPageOffers)
    .pipe(map(offer => offer?.filter(offer => offer.status == OfferStatus.ACCEPTED && offer.offerType != OfferType.FIXED).length > 0));

  hasCreatedOfferForThisProposal$: Observable<boolean> = this.proposalStore.select(selectDetailPageOffers)
    .pipe(map(offer => offer?.filter(offer => offer.creator.id == this.user.id).length > 0));

  isProposalCreator$: Observable<boolean> = this.proposal$.pipe(map(proposal => {
    return proposal?.creator.id === this.user.id;
  }));

  isRepresentativeForProposalCreator: Observable<boolean> = this.proposal$.pipe(map(proposal => {
    return this.user.representedUsers?.some(representedUser => representedUser === proposal?.creator.id);
  }));

  hasLeadingOfferInfo$: Observable<boolean> = this.proposal$.pipe(
    map(proposal => {
      return proposal?.leadingOfferInfo != null
    })
  );

  rejectedProposal$ = this.proposalStore.select(selectDetailPageHasRejected);

  permissions: Permissions[];
  selectedTab$: Observable<DetailsPageTab[]>;
  Tab = DetailsPageTab;
  @Input() adTile: ProposalTile;
  @Input() ad: Proposal;

  Route = Route;
  currentHeader: Header = Header.none;
  proposalId: string = '';

  Header = Header;
  @ViewChild('tabBar') tabBar;
  @ViewChild('title') title;
  @ViewChild('createOfferButton') createOfferButton;
  @ViewChild('header') header;
  @ViewChild(OffersListTabComponent) offersListTabComponent!: OffersListTabComponent;


  ProposalStatus = ProposalStatus;

  constructor(private readonly activatedRoute: ActivatedRoute,
              private readonly router: Router,
              public readonly proposalStore: Store<ProposalAppState>,
              private readonly userStore: Store<UserAppState>,
              public readonly permissionService: PermissionService,
              public cd: ChangeDetectorRef,
              public ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.user$.pipe(take(1)).subscribe(user => this.user = user);

    this.selectedTab$ = this.activatedRoute.queryParamMap.pipe(map(queryMap => {
      const tab = queryMap.get('tab');
      return Object.values<string>(DetailsPageTab).includes(tab) ? tab as DetailsPageTab : DetailsPageTab.DETAILS;
    }), startWith(null), pairwise());
    this.userStore.select(selectUserPermissions).pipe(take(1)).subscribe(permission => this.permissions = permission);

    this.proposalStore.select(selectDetailPageProposalTile);
    this.activatedRoute.params.subscribe(params => {
      if (params['id'] == null) {
        this.router.navigate(['/' + Route.INSERATE]).then();
      } else {
        let proposalId: string = params['id'];
        this.proposalId = proposalId;
        this.proposalStore.dispatch(ProposalDetailPageActions.load({proposalId: proposalId}));
      }
    });

    this.userStore.select(selectUser).pipe(take(1)).subscribe(
      user => {
        this.activeUserId = user.id;
      }
    );

    this.proposalStore.dispatch(ProposalDetailPageActions.loadPrivateNotes({userId: this.activeUserId, proposalId: this.proposalId}));

  }

  changeTab(tab: DetailsPageTab) {
    const queryParams: Params = {tab: tab};
    if (tab == DetailsPageTab.DETAILS) {
      this.allDetailsOpen = false;
    }
    if (tab == DetailsPageTab.OFFERS) {
      this.offersListTabComponent.resetStateToDefault();
    }
    this.ngZone.run(() =>
      this.router.navigate(
        [],
        {
          relativeTo: this.activatedRoute,
          queryParams: queryParams,
          queryParamsHandling: 'merge', // remove to replace all query params by provided
        }).then()).then();
  }


  hasPermission(tab: DetailsPageTab): boolean {
    return this.permissions.includes(this.validPermissions(tab));
  }


  validPermissions(tab: DetailsPageTab): Permissions {
    if (DetailsPageTab.DETAILS === tab) {
      return Permissions.proposal_read;
    }
    if (DetailsPageTab.LEADING_OFFER === tab) {
      return Permissions.proposal_read;
    }
    if (DetailsPageTab.CREATE_OFFER === tab) {
      return Permissions.offer_create;
    }
    if (DetailsPageTab.OFFERS === tab) {
      return Permissions.offer_read;
    }
    if (DetailsPageTab.SLIP === tab) {
      return Permissions.proposal_read;
    }
    if (DetailsPageTab.VERSION_HISTORY === tab) {
      return Permissions.proposal_read;
    }
  }

  onWindowScroll() {
    const tabBarScrolled = this.tabBar?.nativeElement.getBoundingClientRect().top;
    const createOfferScrolled = this.createOfferButton?.nativeElement.getBoundingClientRect().top ?? 0;
    const titleScrolled = this.title?.nativeElement.getBoundingClientRect().top;
    const headerHeight = this.header?.nativeElement.getBoundingClientRect().height ?? 0;
    let oldHeader = this.currentHeader;
    if (0 < titleScrolled) {
      this.currentHeader = Header.none;
    }
    if (titleScrolled < 0) {
      this.currentHeader = Header.title;
    }
    if (createOfferScrolled < 0) {
      this.currentHeader = Header.createOffer;
    }

    if (tabBarScrolled < headerHeight) {
      this.currentHeader = Header.tabs;
    }
    if (oldHeader != this.currentHeader) {
      this.cd.detectChanges();
    }
  }

  navigateToCreateOffer(proposalId): void {
    this.ngZone.run(() => {
      this.router.navigate(['/' + Route.CREATE_OFFER, proposalId]).then();
    });
  }

  protected readonly Roles = Roles;
}

export enum Header {
  'none',
  'title',
  'createOffer',
  'tabs'
}
